import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Shuffle, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nome: "", produto_ids: [], permitir_substituicao: true, ativo: true };

export default function GruposSubstituicao() {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "MASTER", "GESTOR", "SUPERVISOR"].includes(user.role);
  const canExcluir = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [grupos, setGrupos] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/grupos-substituicao").then((r) => setGrupos(r.data));
  useEffect(() => { load(); api.get("/produtos").then((r) => setProdutos(r.data.filter((p) => p.ativo !== false))); }, []);

  const open = (g) => {
    if (g) { setForm({ nome: g.nome, produto_ids: g.produto_ids || [], permitir_substituicao: g.permitir_substituicao, ativo: g.ativo }); setEditId(g.id); }
    else { setForm(EMPTY); setEditId(null); }
    setQ("");
    setModal(true);
  };

  const toggleProd = (id) => setForm((f) => ({ ...f, produto_ids: f.produto_ids.includes(id) ? f.produto_ids.filter((x) => x !== id) : [...f.produto_ids, id] }));

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome do grupo");
    if (form.produto_ids.length < 2) return toast.error("Selecione ao menos 2 produtos substitutos");
    setSaving(true);
    try {
      if (editId) await api.put(`/grupos-substituicao/${editId}`, form);
      else await api.post("/grupos-substituicao", form);
      toast.success(editId ? "Grupo atualizado" : "Grupo criado");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const del = async (g) => {
    if (!window.confirm(`Excluir o grupo "${g.nome}"?`)) return;
    try { await api.delete(`/grupos-substituicao/${g.id}`); toast.success("Grupo excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const filtered = produtos.filter((p) => `${p.nome} ${p.sku} ${p.marca} ${p.ean}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="grupos-sub-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Grupos de Substituição</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Defina quais produtos podem substituir uns aos outros durante a separação. Cada produto mantém SKU, EAN, estoque e histórico próprios.</p>
        </div>
        <Button onClick={() => open(null)} data-testid="btn-novo-grupo"><Plus className="w-4 h-4" /> Novo Grupo</Button>
      </div>

      {!grupos ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : grupos.length === 0 ? (
        <Card className="bg-card/70 p-8 text-center text-muted-foreground" data-testid="grupos-vazio">Nenhum grupo de substituição cadastrado.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {grupos.map((g) => (
            <Card key={g.id} className="bg-card/70 p-4" data-testid={`grupo-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Shuffle className="w-4 h-4 text-primary shrink-0" />
                    <div className="font-semibold truncate">{g.nome}</div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm font-mono border ${g.permitir_substituicao ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-slate-500/15 text-slate-300 border-slate-500/40"}`}>
                      {g.permitir_substituicao ? "Substituição ON" : "Substituição OFF"}
                    </span>
                    {g.ativo === false && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 font-mono">Inativo</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => open(g)} data-testid={`edit-grupo-${g.id}`}><Pencil className="w-4 h-4" /></Button>
                  {canExcluir && <Button variant="ghost" size="icon" className="text-red-400" onClick={() => del(g)} data-testid={`del-grupo-${g.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="mt-3 space-y-1">
                {(g.produtos || []).map((p) => (
                  <div key={p.id} className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <span className="font-mono text-primary">{p.sku}</span> · {p.nome}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar" : "Novo"} Grupo de Substituição</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome do grupo</Label><Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} placeholder="Ex.: Creme de Leite 200g" data-testid="f-grupo-nome" /></div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div><div className="text-sm font-medium">Permitir substituição na separação</div><div className="text-xs text-muted-foreground">Se desligado, os produtos ficam vinculados mas não substituem na bipagem.</div></div>
              <Switch checked={form.permitir_substituicao} onCheckedChange={(v) => setForm((f) => ({ ...f, permitir_substituicao: v }))} data-testid="f-grupo-permitir" />
            </div>
            <div>
              <Label>Produtos do grupo <span className="text-muted-foreground font-normal normal-case">({form.produto_ids.length} selecionado(s))</span></Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, SKU, marca ou EAN" className="mb-2" data-testid="f-grupo-busca" />
              <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border">
                {filtered.map((p) => {
                  const sel = form.produto_ids.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => toggleProd(p.id)} data-testid={`f-grupo-prod-${p.sku}`}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm ${sel ? "bg-primary/10" : "hover:bg-secondary/40"}`}>
                      <div className="min-w-0"><div className="truncate">{p.nome}</div><div className="text-[11px] font-mono text-muted-foreground">{p.sku} · {p.marca}</div></div>
                      {sel && <Check className="w-4 h-4 text-primary shrink-0" />}
                    </button>
                  );
                })}
                {!filtered.length && <div className="px-3 py-4 text-xs text-muted-foreground text-center">Nenhum produto encontrado.</div>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-grupo">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
